import { useMsal } from '@azure/msal-react';
import { Text } from '@fluentui/react-components';
import { ChevronRight16Regular } from '@fluentui/react-icons';
import { parseISO } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router';
import { downloadFile } from '../../common/file/download-file';
import { useStores, useTitle } from '../../common/hooks';
import { ResourceTypes } from '../../models/resource-services';
import { RoutePaths } from '../../route-paths';
import { Notifications } from '../shared-components/Notifications';
import { PageTitle } from '../shared-components';
import { useAppToast } from '../../shared/toast';
import { FlexBox } from '../shared-components/flex-box';
import { ExportNominationsModal, ExportType } from '../shared-components/export-nominations-modal/ExportNominationsModal';
import './NominationDashboardPage.scss';
import { AwardedNominations } from './components/AwardedNominations';
import { NominationSummaries } from './components/NominationSummaries';
import inspire from '/assets/images/2019_MSFTInspire_Batch_OSA6596_sRGB.jpg';
import instruction from '/assets/images/Award Resources.png';
import nomination from '/assets/images/Create a Nomination.png';

interface NominationDashboardPageProps {
  isShowSubmitNominationSuccessToast: boolean;
  programId: number;
}

const NominationDashboardPage: React.FC<NominationDashboardPageProps> = (props) => {
  const {
    isShowSubmitNominationSuccessToast,
    programId,
  } = props;

  const {
    instance
  } = useMsal();

  const {
    apiStore,
    apiEndpointCallStatesStore,
    notificationStore,
    awardStore,
    userStore,
    nominationDashboardPageUiStore,
  } = useStores();

  const [isLoadingNomination, setLoadingNomination] = useState(true);
  const [selectedNominationId, setSelectedNominationId] = useState<number>(0);
  const [isShowExportNominationModal, setShowExportNominationModal] = useState<boolean>(false);

  const { showToast } = useAppToast();

  const isDownloadServiceInFlight = apiEndpointCallStatesStore.inFlightStatusMap.get('postResourceTicket') ?? false;

  const getNominationSummaries = async (): Promise<void> => {
    try {
      const summaries = await apiStore.getNominationSummaries(instance);
      nominationDashboardPageUiStore.setNominationSummaries(summaries);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardedNominations = async (): Promise<void> => {
    if (userStore.userInfo?.isWinner) {
      try {
        const awarded = await apiStore.getAwardedNominations(instance);
        nominationDashboardPageUiStore.setAwardedNominations(awarded);
      } catch (error) {
        // TODO - handle error
      }
    }
  };

  const deleteNomination = async (nominationId: number): Promise<void> => {
    try {
      await apiStore.deleteNomination(instance, nominationId);
      await getNominationSummaries();
    } catch (error) {
      // TODO - handle error
    }
  }

  const handleExportNomination = async (exportedType: ExportType): Promise<void> => {
    if (selectedNominationId > 0) {
      try {
        const resourceTicket = await apiStore.postResourceTicket(instance, {
          resourceType: ResourceTypes.ExportedNomination,
          data: {
            routeData: {
              nominationId: selectedNominationId,
            },
            queryString: {
              exportType: exportedType,
            }
          },
        });
        downloadFile(resourceTicket.ticketId);
      } catch (error) {
        // TODO - handle error
      }
    }
    setShowExportNominationModal(false);
    setSelectedNominationId(0);
  }

  const preloadData = async (): Promise<void> => {
    setLoadingNomination(true);
    try {
      await Promise.all([
        getNominationSummaries(),
        getAwardedNominations(),
      ]);

      if (programId) {
        const notifications = await apiStore.getProgramNotifications(instance, programId, 'partner-homepage');
        notificationStore.setNotifications('partner-homepage', notifications);
      }
    } finally {
      setLoadingNomination(false);
    }
  }

  useTitle('Partner of the Year Awards Main Dashboard');
  useEffect(() => {
    preloadData();
    return () => {
      nominationDashboardPageUiStore.resetStates();
    };
  }, [programId]);
  useEffect(() => {
    if (!isShowSubmitNominationSuccessToast) {
      return;
    }

    const timeoutId = setTimeout(() => {
      // Adding a timeout to prevent screen reader conflict with the initial landmark call(first page render)
      showToast({
        title: 'Your Nomination was successfully submitted!',
        intent: 'success',
      });
    }, 150);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isShowSubmitNominationSuccessToast]);

  const renderNominationDeadline = (): React.JSX.Element | undefined => {
    if (!awardStore.nominationWindowProgramDate) return undefined;

    const pstDate = utcToZonedTime(parseISO(awardStore.nominationWindowProgramDate.displayEndDate), 'America/Los_Angeles');
    const pattern = 'z'
    const timeZoneAbbr = format(pstDate, pattern, { timeZone: 'America/Los_Angeles' })
    return (
      <Text
        block
      >
        {`Nomination deadline: ${format(pstDate, 'MM/dd/yyyy hh:mma')} ${timeZoneAbbr}`}
      </Text>
    );
  }

  const renderMenuItemBox = (
    imageSrc: string,
    imageAltText: string,
    classNameText: string,
    title: string,
    highlightedTitle: string,
    customDetails?: React.JSX.Element,
    disabled?: boolean,
    disabledInfo?: string
  ): React.JSX.Element => {
    return (
      <>
        <figure>
          <img
            width={416}
            height={209}
            src={imageSrc}
            alt={imageAltText}
          />
        </figure>
        <FlexBox
          direction='column'
          content='space-between'
          className={classNameText}
        >
          <FlexBox
            direction='column'
          >
            <Text
              className='item-box-content-title'
              size={700}
              weight='semibold'
              block
            >
              {title}
              <Text
                size={700}
                weight='semibold'
                className='title--highlighted'
              >
                {highlightedTitle}
              </Text>
            </Text>
            {customDetails}
          </FlexBox>
          {
            disabled ?
              <Text
                size={400}
                italic
              >
                {disabledInfo}
              </Text> :
              <Text
                size={400}
                block
              >
                {'Learn more '}
                <ChevronRight16Regular />
              </Text>
          }
        </FlexBox>
      </>);
  };

  const renderMenuItem = (
    imageSrc: string,
    imageAltText: string,
    classNameText: string,
    title: string,
    highlightedTitle: string,
    route: RoutePaths,
    customDetails?: React.JSX.Element,
    disabled?: boolean,
    openInNewTab?: boolean,
    disabledInfo?: string
  ): React.JSX.Element => {
    return (
      <>
        {
          openInNewTab ?
            <a
              href={route}
              target={'_blank'}
              rel='noopener noreferrer'
              className='item-box'
            >
              {renderMenuItemBox(imageSrc, imageAltText, classNameText, title, highlightedTitle, customDetails, disabled, disabledInfo)}
            </a> :
            <NavLink
              onClick={e => {
                if (disabled) e.preventDefault();
              }}
              className='item-box'
              to={route}
              end
            >
              {renderMenuItemBox(imageSrc, imageAltText, classNameText, title, highlightedTitle, customDetails, disabled, disabledInfo)}
            </NavLink>
        }
      </>
    );
  };

  return (
    <main
      className='nomination-dashboard-page'
    >
      {
        isShowExportNominationModal &&
        <ExportNominationsModal
          isModalOpen={isShowExportNominationModal}
          isDownloading={isDownloadServiceInFlight}
          onExportButtonClicked={handleExportNomination}
          onCancelButtonClicked={() => {
            setShowExportNominationModal(false);
            setSelectedNominationId(0);
          }}
        />
      }
      <PageTitle
        title='Partner of the Year Awards'
        subtitle='Microsoft AI Cloud Partner Program'
      />
      <div
        className='nomination-dashboard-page__description'
      >
        <Text
          as='p'
          block
        >
          Welcome to the 2026 Microsoft Partner of the Year Awards and Country/Region Partner of the Year Awards.
        </Text>
        <Text
          as='p'
          block
        >
          We are excited to offer Microsoft partners a variety of awards designed to showcase solutions delivering customer success, that are built on Microsoft cloud and platforms. These awards celebrate partner achievements, cloud technologies, entrepreneurial spirit, and highlight the tremendous work done by partners in various industries and in driving social impact. The Country/Region Partner of the Year Awards recognize partner successes in countries/regions around the world.
        </Text>
      </div>
      {
        notificationStore.dashboardPageNotifications('partner-homepage').length > 0 &&
        <Notifications
          notifications={notificationStore.dashboardPageNotifications('partner-homepage')}
        />
      }
      <FlexBox
        as='section'
        wrap
        className='nomination-dashboard-page__menu'
      >
        {userStore.userInfo?.isWinner === null && renderMenuItem(nomination, 'Create a nomination', 'item-box-content create-nomination', 'Create a ', 'nomination', RoutePaths.SelectAward, renderNominationDeadline(), !awardStore.nominationWindowProgramDate, false, 'The nomination window is closed. Please check back later for updates.')}
        {renderMenuItem(instruction, 'Review award resources', 'item-box-content review-information', userStore.userInfo?.isWinner ? 'Access ' : 'Review ', userStore.userInfo?.isWinner ? 'winner and finalist resources' : 'award resources', RoutePaths.AwardResources)}
        {renderMenuItem(inspire, 'Explore microsoft ignite', 'item-box-content explore-inspire', 'Explore ', 'Microsoft Ignite', RoutePaths.Inspire, undefined, false, true)}
      </FlexBox>
      {
        userStore.userInfo?.isWinner && (
          <section>
            <AwardedNominations />
          </section>
        )
      }
      <NominationSummaries
        onDelete={deleteNomination}
        onDownload={(nominationId) => {
          setSelectedNominationId(nominationId);
          setShowExportNominationModal(true);
        }}
        isNominationWindowClosed={!awardStore.nominationWindowProgramDate}
        isLoadingNomination={isLoadingNomination}
      />
    </main>
  );
};

export default observer(NominationDashboardPage);
